import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $nodesOfType,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from "lexical";
import { useEffect } from "react";

import {
  INSERT_OFFER_COMMAND,
  REMOVE_OFFER_COMMAND,
  type InsertOfferPayload,
} from "./offerCommands";
import { $createOfferNode, $isOfferNode, OfferNode } from "./OfferNode";

interface OfferCommandPluginProps {
  /**
   * Called when the editor's last offer chip is removed without a replacement
   * in the same update. Hosts that mirror the offer into external state (e.g.
   * the message-draft store) use this to clear it; surfaces with no such state
   * omit it.
   */
  onOfferRemoved?: () => void;
}

/**
 * Offer insert/remove + keyboard-deletion handling, free of any external store.
 * `OfferPlugin` wraps this to sync the message-draft store; `InlineLexicalInput`
 * renders it directly. Keeping one implementation means the bug-prone
 * keyboard-deletion logic doesn't live in two copies that can drift.
 */
export function OfferCommandPlugin({
  onOfferRemoved,
}: OfferCommandPluginProps = {}): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onOfferRemoved) return;
    return editor.registerMutationListener(OfferNode, (mutations) => {
      let hasDestroyed = false;
      let hasCreated = false;
      for (const [, mutation] of mutations) {
        if (mutation === "destroyed") hasDestroyed = true;
        if (mutation === "created") hasCreated = true;
      }
      if (hasDestroyed && !hasCreated) {
        onOfferRemoved();
      }
    });
  }, [editor, onOfferRemoved]);

  useEffect(() => {
    return editor.registerCommand<InsertOfferPayload>(
      INSERT_OFFER_COMMAND,
      (payload) => {
        const existingOffers = $nodesOfType(OfferNode);

        const newNode = $createOfferNode(
          payload.slug,
          payload.label,
          payload.fallbackCode,
        );

        if (existingOffers.length > 0) {
          existingOffers[0].replace(newNode);
          for (let i = 1; i < existingOffers.length; i++) {
            existingOffers[i].remove();
          }
        } else {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes([newNode]);
          } else {
            const root = $getRoot();
            const lastChild = root.getLastChild();
            if ($isElementNode(lastChild)) {
              lastChild.append(newNode);
            } else {
              root.append(newNode);
            }
          }
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand<{ slug: string }>(
      REMOVE_OFFER_COMMAND,
      (payload) => {
        const matches = $nodesOfType(OfferNode).filter(
          (node) => node.__slug === payload.slug,
        );
        for (const node of matches) {
          node.remove();
        }
        return matches.length > 0;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          let handled = false;
          for (const node of nodes) {
            if ($isOfferNode(node)) {
              node.remove();
              handled = true;
            }
          }
          if (handled) {
            event?.preventDefault();
            return true;
          }
        }

        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const { anchor } = selection;
          const anchorNode = anchor.getNode();
          const anchorOffset = anchor.offset;

          if ($isElementNode(anchorNode)) {
            const childAtOffset = anchorNode.getChildAtIndex(anchorOffset - 1);
            if ($isOfferNode(childAtOffset)) {
              childAtOffset.remove();
              event?.preventDefault();
              return true;
            }
          }

          if (anchorOffset === 0) {
            const prevSibling = anchorNode.getPreviousSibling();
            if ($isOfferNode(prevSibling)) {
              prevSibling.remove();
              event?.preventDefault();
              return true;
            }
          }

          if ($isOfferNode(anchorNode)) {
            anchorNode.remove();
            event?.preventDefault();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      KEY_DELETE_COMMAND,
      (event) => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes();
          let handled = false;
          for (const node of nodes) {
            if ($isOfferNode(node)) {
              node.remove();
              handled = true;
            }
          }
          if (handled) {
            event?.preventDefault();
            return true;
          }
        }

        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const { anchor } = selection;
          const anchorNode = anchor.getNode();
          const anchorOffset = anchor.offset;

          if ($isElementNode(anchorNode)) {
            const childAtOffset = anchorNode.getChildAtIndex(anchorOffset);
            if ($isOfferNode(childAtOffset)) {
              childAtOffset.remove();
              event?.preventDefault();
              return true;
            }
          }

          const textContentLength = anchorNode.getTextContentSize();
          if (anchorOffset === textContentLength) {
            const nextSibling = anchorNode.getNextSibling();
            if ($isOfferNode(nextSibling)) {
              nextSibling.remove();
              event?.preventDefault();
              return true;
            }
          }

          if ($isOfferNode(anchorNode)) {
            anchorNode.remove();
            event?.preventDefault();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  return null;
}
